/**
 * Phase 8 — Winhouse Journey Map Card.
 * Compact-first: renders a current-stage + overall progress preview with an
 * "Explore Journey" toggle that expands the 13-stage list inline.
 * Program-agnostic — driven entirely by /api/me/student/journey response.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Compass, Calculator, BookOpen, Sigma, PenLine, Zap, Feather, Timer,
  Trophy, Sparkles, Users, ShieldCheck, Rocket, Circle,
  Lock, CheckCircle2, Play, ArrowRight, Route, ChevronDown, ChevronUp,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const ICONS = {
  Compass, Calculator, BookOpen, Sigma, PenLine, Zap, Feather, Timer,
  Trophy, Sparkles, Users, ShieldCheck, Rocket, Circle,
};

const STATUS_META = {
  locked:      { label: 'Locked',       color: 'text-gray-400', bg: 'bg-gray-50 border-gray-100',    icon: Lock },
  unlocked:    { label: 'Unlocked',     color: 'text-blue-700', bg: 'bg-blue-50 border-blue-100',    icon: Play },
  in_progress: { label: 'In progress',  color: 'text-amber-700', bg: 'bg-amber-50 border-amber-100', icon: Play },
  completed:   { label: 'Completed',    color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-100', icon: CheckCircle2 },
};

const StageRow = ({ s }) => {
  const meta = STATUS_META[s.status] || STATUS_META.locked;
  const StageIcon = ICONS[s.icon] || Circle;
  const StatusIcon = meta.icon;
  return (
    <div className={`flex items-start gap-3 rounded-xl border p-3 ${meta.bg}`} data-testid={`journey-stage-${s.key}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-white border ${meta.color}`}>
        <StageIcon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-bold text-sm text-gray-900 truncate">{s.order}. {s.title}</div>
          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold ${meta.color}`}>
            <StatusIcon className="h-3 w-3" /> {meta.label}
          </span>
        </div>
        <div className="text-[11px] text-gray-500 mt-0.5">{s.subtitle}</div>
        <div className="mt-2 h-1.5 rounded-full bg-white/70 overflow-hidden">
          <div
            className={`h-full ${s.status === 'completed' ? 'bg-emerald-500' : s.status === 'in_progress' ? 'bg-amber-500' : s.status === 'unlocked' ? 'bg-blue-500' : 'bg-gray-300'}`}
            style={{ width: `${s.progress_pct}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-2 text-[10px] text-gray-600">
          <span data-testid={`journey-progress-${s.key}`}>{s.progress_pct}% · {s.current_value}/{s.target}</span>
          {s.badges_earned?.length > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-700 font-bold">
              <Trophy className="h-3 w-3" /> {s.badges_earned.length} badge{s.badges_earned.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        {s.status !== 'locked' && s.status !== 'completed' && s.next_action_url && (
          <Link to={s.next_action_url}
            className="inline-flex items-center gap-1 mt-2 text-[11px] font-bold text-red-700 hover:text-red-900"
            data-testid={`journey-cta-${s.key}`}>
            {s.next_action_label || 'Continue'} <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>
    </div>
  );
};

const JourneyMapCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await ax.get('/api/me/student/journey');
        setData(r.data);
      } catch (_e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="journey-map-loading">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-100 rounded w-1/3" />
          <div className="h-2 bg-gray-100 rounded w-full" />
          <div className="h-14 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }
  if (!data) return null;
  if (data.coming_soon) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="journey-map-coming-soon">
        <div className="flex items-center gap-2 text-red-700 font-bold">
          <Route className="h-4 w-4" /> Journey Map
        </div>
        <div className="mt-2 text-sm text-gray-600">
          The {data.program_name} journey is coming soon. Meanwhile, keep learning and your XP will carry over.
        </div>
      </div>
    );
  }
  const cur = data.current_stage;
  const CurIcon = ICONS[cur?.icon] || Circle;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="journey-map-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-red-700 flex items-center gap-1.5">
            <Route className="h-3 w-3" /> Your Journey · {data.program_name}
          </div>
          <div className="text-base font-bold mt-1" data-testid="journey-title">
            Stage {data.current_stage_index + 1} of {data.stages_total}: {cur?.title}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">{cur?.subtitle}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Overall</div>
          <div className="text-lg font-bold text-red-700" data-testid="journey-overall-pct">{data.overall_progress_pct}%</div>
          <div className="text-[10px] text-gray-500">{data.stages_completed}/{data.stages_total} stages</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden mb-4">
        <div
          className="h-full bg-gradient-to-r from-red-600 via-amber-500 to-emerald-500 transition-all"
          style={{ width: `${data.overall_progress_pct}%` }}
        />
      </div>

      {/* Current stage highlight */}
      {cur && (
        <div className="rounded-xl bg-gradient-to-br from-black to-gray-800 text-white p-4 mb-3" data-testid="journey-next-milestone">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D3D86F] text-black flex items-center justify-center">
              <CurIcon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-[#D3D86F] font-bold">Next milestone</div>
              <div className="font-bold text-sm truncate">{cur.title}</div>
              <div className="text-[11px] text-white/70 truncate">{cur.subtitle}</div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Link
              to={cur.next_action_url || '/student/learning'}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-[#D3D86F] text-black text-xs font-bold uppercase tracking-wider"
              data-testid="journey-continue-btn">
              Continue Journey <ArrowRight className="h-3 w-3" />
            </Link>
            <button
              onClick={() => setExpanded(v => !v)}
              className="inline-flex items-center gap-1 px-3 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold uppercase tracking-wider"
              data-testid="journey-expand-btn">
              {expanded ? (<>Hide stages <ChevronUp className="h-3 w-3" /></>) : (<>See all stages <ChevronDown className="h-3 w-3" /></>)}
            </button>
          </div>
        </div>
      )}

      {expanded && (
        <div className="space-y-2 mt-2" data-testid="journey-stage-list">
          {data.stages.map(s => <StageRow key={s.key} s={s} />)}
        </div>
      )}
    </div>
  );
};

export default JourneyMapCard;
